import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as PerformanceIcon,
  Storage as DatabaseIcon
} from '@mui/icons-material';

function SystemHealthPanel({ health }) {
  if (!health) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Loading system health...
        </Typography>
      </Paper>
    );
  }

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getProgressColor = (percent) => {
    if (percent < 70) return 'success';
    if (percent < 85) return 'warning';
    return 'error';
  };

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${units[i]}`;
  };

  return (
    <Grid container spacing={3}>
      {/* Database Health */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <DatabaseIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Database</Typography>
          </Box>
          
          {health.database && (
            <>
              <Chip
                label={health.database.status}
                color={getHealthColor(health.database.status)}
                size="small"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" color="text.secondary">
                Type: {health.database.type}
              </Typography>
              {health.database.size && (
                <Typography variant="body2" color="text.secondary">
                  Size: {health.database.size}
                </Typography>
              )}
              {health.database.connections !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  Connections: {health.database.connections}
                </Typography>
              )}
              {health.database.slowQueries !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  Slow Queries: {health.database.slowQueries}
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Grid>

      {/* Disk Health */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Disk Space</Typography>
          </Box>
          
          {health.disk && (
            <>
              <Chip
                label={health.disk.status}
                color={getHealthColor(health.disk.status)}
                size="small"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {health.disk.used}
                {health.disk.totalBytes ? ` / ${health.disk.total}` : ''}
              </Typography>

              {health.disk.totalBytes ? (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={health.disk.usagePercent}
                    color={getProgressColor(health.disk.usagePercent)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {health.disk.usagePercent}% used
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Sum of persistent database storage
                </Typography>
              )}

              {health.breakdown && health.breakdown.disk && health.breakdown.disk.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {health.breakdown.disk.map((d) => (
                    <Typography key={d.service} variant="caption" color="text.secondary" display="block">
                      {d.service}: {d.used}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Grid>

      {/* Memory Health */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Memory</Typography>
          </Box>

          {health.memory && (
            <>
              <Chip
                label={health.memory.status}
                color={getHealthColor(health.memory.status)}
                size="small"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {health.memory.used} / {health.memory.total}
              </Typography>

              {health.memory.totalBytes ? (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={health.memory.usagePercent}
                    color={getProgressColor(health.memory.usagePercent)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {health.memory.usagePercent}% used
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No container memory limit detected
                </Typography>
              )}

              {health.breakdown && health.breakdown.memory && health.breakdown.memory.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {health.breakdown.memory.map((m) => (
                    <Typography key={m.service} variant="caption" color="text.secondary" display="block">
                      {m.service}: {formatBytes(m.usedBytes)}
                      {m.limitBytes ? ` / ${formatBytes(m.limitBytes)}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Grid>

      {/* Performance */}
      <Grid item xs={12} sm={6} md={3}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <PerformanceIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Performance</Typography>
          </Box>
          
          {health.performance && (
            <>
              <Chip
                label={health.performance.status}
                color={getHealthColor(health.performance.status)}
                size="small"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" color="text.secondary">
                Uptime: {health.performance.uptime}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CPU: {health.performance.cpuUsage}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cores: {health.performance.cpuCores}
              </Typography>
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default SystemHealthPanel;

